// bluaIQ — Reusable canvas signature pad.
// Originally inlined in ContractSignPage.tsx for the contractor
// public-sign flow; extracted in Phase 2 of the contract signing
// lifecycle so the authenticated builder/owner countersign modal
// in ContractsModule.tsx can reuse the same draw + export logic.
//
// The component is purely presentational: it owns the drawing
// state but calls back to the parent with a base64 PNG data URL
// each time the user finishes a stroke, or with `null` on clear.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Eraser } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export default function SignaturePad({
  onSignature,
}: {
  onSignature: (data: string | null) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasDrawn, setHasDrawn] = useState(false)
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * 2
    canvas.height = rect.height * 2
    ctx.scale(2, 2)
    ctx.strokeStyle = '#1C1A17'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [])

  const getPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top }
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top }
  }, [])

  const startDraw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault()
      const ctx = canvasRef.current?.getContext('2d')
      if (!ctx) return
      const pos = getPos(e)
      ctx.beginPath()
      ctx.moveTo(pos.x, pos.y)
      setIsDrawing(true)
    },
    [getPos],
  )

  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing) return
      e.preventDefault()
      const ctx = canvasRef.current?.getContext('2d')
      if (!ctx) return
      const pos = getPos(e)
      ctx.lineTo(pos.x, pos.y)
      ctx.stroke()
      setHasDrawn(true)
    },
    [isDrawing, getPos],
  )

  const endDraw = useCallback(() => {
    setIsDrawing(false)
    if (hasDrawn && canvasRef.current) {
      onSignature(canvasRef.current.toDataURL('image/png'))
    }
  }, [hasDrawn, onSignature])

  const clear = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasDrawn(false)
    onSignature(null)
  }

  return (
    <div className="space-y-2">
      <div className="relative border border-[#ECEAE4] rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          className="w-full h-[120px] cursor-crosshair touch-none"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!hasDrawn && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-[11px] font-[600] text-[#ECEAE4]">
              {isEs ? 'Firme aquí' : 'Sign here'}
            </span>
          </div>
        )}
      </div>
      {hasDrawn && (
        <button
          type="button"
          onClick={clear}
          className="flex items-center gap-1 text-[12px] font-[700] text-[#9E9890] hover:text-[#1C1A17]"
        >
          <Eraser size={12} /> {isEs ? 'Borrar firma' : 'Clear signature'}
        </button>
      )}
    </div>
  )
}
